import {
  Building2,
  ClipboardList,
  FileSpreadsheet,
  History,
  LayoutDashboard,
  Map,
  Megaphone,
  Phone,
  ScanLine,
  Settings,
  ShieldAlert,
  Siren,
  Sprout,
  Tag,
  Users,
  UserCog,
  Gauge,
  CloudRain,
  Inbox,
  FileText,
  Receipt,
  BellRing,
  MessageSquareHeart,
  Construction,
  CalendarX,
  BookOpenText,
  ShoppingBasket,
  Recycle,
  Building,
  ShieldCheck,
  Mountain,
  Scale,
  Gavel,
  MonitorDot,
  type LucideIcon,
} from 'lucide-react';
import { useEffect } from 'react';
import { Link, NavLink, Navigate, Outlet, useLocation, useNavigate, useOutletContext, useParams } from 'react-router';
import { roleAtLeast, type Capability, type Membership, type OfficeRole } from '@ili/shared';
import { IliMark } from '../components/Brand';
import { useT } from '../i18n';
import { useMe } from '../lib/auth';
import { safeStorage } from '../lib/device';
import { useSettings } from '../lib/settings';

export type StaffOffice = {
  membership: Membership;
  officeId: number;
  role: OfficeRole;
  can: (cap: Capability) => boolean;
  atLeast: (role: OfficeRole) => boolean;
  /** Base API path for this office, e.g. /staff/offices/12 */
  api: string;
  /** Base app path, e.g. /staff/12 */
  base: string;
};

const LAST_OFFICE = 'ili.staff.office';
const store = safeStorage();

export function useStaffOffice(): StaffOffice {
  const ctx = useOutletContext<StaffOffice | null>();
  if (!ctx) throw new Error('useStaffOffice outside an office route');
  return ctx;
}

type NavItem = { to: string; label: string; icon: LucideIcon; show?: boolean };

/** Redirects /staff to the last used (or first) office. */
export function StaffIndexRedirect() {
  const { me } = useMe();
  const t = useT();
  if (!me) return null;
  const last = Number(store.get(LAST_OFFICE));
  const m = me.memberships.find((x) => x.officeId === last) ?? me.memberships[0];
  if (m) return <Navigate to={`/staff/${m.officeId}`} replace />;
  if (me.isAdmin) return <Navigate to="/admin" replace />;
  return (
    <div className="page page-narrow">
      <div className="card card-pad-lg stack">
        <h1>{t('staff.noOfficeTitle')}</h1>
        <p className="muted">{t('staff.noOfficeBody')}</p>
        <Link to="/" className="btn btn-secondary">
          {t('nav.residentApp')}
        </Link>
      </div>
    </div>
  );
}

export function StaffShell({ mode }: { mode: 'office' | 'admin' }) {
  const t = useT();
  const s = useSettings();
  const { me } = useMe();
  const params = useParams();
  const nav = useNavigate();
  const loc = useLocation();
  const officeId = Number(params.officeId);
  const membership = me?.memberships.find((m) => m.officeId === officeId) ?? null;

  useEffect(() => {
    if (membership) store.set(LAST_OFFICE, String(membership.officeId));
  }, [membership]);

  if (!me) return null;
  if (mode === 'office' && !membership) return <Navigate to="/staff" replace />;

  const ctx: StaffOffice | null = membership
    ? {
        membership,
        officeId,
        role: membership.role,
        can: (c) => membership.office.capabilities.includes(c),
        atLeast: (r) => roleAtLeast(membership.role, r),
        api: `/staff/offices/${officeId}`,
        base: `/staff/${officeId}`,
      }
    : null;

  const officeNav: NavItem[] = ctx
    ? [
        { to: ctx.base, label: t('staff.navDashboard'), icon: LayoutDashboard },
        { to: `${ctx.base}/posts`, label: t('staff.navPosts'), icon: Megaphone },
        { to: `${ctx.base}/requests`, label: t('staff.navRequests'), icon: Inbox },
        { to: `${ctx.base}/services`, label: t('staff.navServices'), icon: FileText },
        { to: `${ctx.base}/checkin`, label: t('staff.navCheckin'), icon: ScanLine },
        { to: `${ctx.base}/hq`, label: t('staff.navHq'), icon: MonitorDot, show: ctx.can('dispatch') || ctx.membership.office.type === 'barangay' },
        { to: `${ctx.base}/dispatch`, label: t('staff.navDispatch'), icon: Siren, show: ctx.can('dispatch') },
        { to: `${ctx.base}/crop`, label: t('staff.navCrop'), icon: Sprout, show: ctx.can('crop_damage') },
        { to: `${ctx.base}/safety`, label: t('staff.navSafety'), icon: Map, show: ctx.can('evacuation') },
        { to: `${ctx.base}/emergency-mode`, label: t('staff.navEmergencyMode'), icon: ShieldAlert, show: ctx.can('emergency_mode') && ctx.atLeast('editor') },
        { to: `${ctx.base}/alerts`, label: t('staff.navAlerts'), icon: BellRing, show: ctx.can('town_wide_urgent_alerts') },
        { to: `${ctx.base}/revenue`, label: t('staff.navRevenue'), icon: Receipt, show: ctx.can('tax') },
        { to: `${ctx.base}/feedback`, label: t('staff.navFeedback'), icon: MessageSquareHeart },
        { to: `${ctx.base}/guides`, label: t('staff.navGuides'), icon: BookOpenText },
        { to: `${ctx.base}/market-prices`, label: t('staff.navMarketPrices'), icon: ShoppingBasket, show: ctx.can('market_prices') },
        { to: `${ctx.base}/recycling`, label: t('staff.navRecycling'), icon: Recycle, show: ctx.can('waste') },
        { to: `${ctx.base}/facilities`, label: t('staff.navFacilities'), icon: Building, show: ctx.can('facilities') },
        { to: `${ctx.base}/moderation`, label: t('staff.navModeration'), icon: ShieldCheck, show: ctx.can('community_moderation') },
        { to: `${ctx.base}/tourism`, label: t('staff.navTourism'), icon: Mountain, show: ctx.can('tourism') },
        { to: `${ctx.base}/transparency`, label: t('staff.navTransparency'), icon: Scale, show: ctx.can('transparency') },
        { to: `${ctx.base}/ordinances`, label: t('staff.navOrdinances'), icon: Gavel, show: ctx.can('ordinances') },
        { to: `${ctx.base}/profile`, label: t('staff.navProfile'), icon: Building2, show: ctx.atLeast('office_head') },
        { to: `${ctx.base}/team`, label: t('staff.navTeam'), icon: Users, show: ctx.atLeast('office_head') },
      ]
    : [];
  const adminNav: NavItem[] = me.isAdmin
    ? [
        { to: '/admin', label: t('staff.navAdminHome'), icon: Gauge },
        { to: '/admin/offices', label: t('staff.navOffices'), icon: Building2 },
        { to: '/admin/staff', label: t('staff.navStaffAccounts'), icon: UserCog },
        { to: '/admin/settings', label: t('staff.navSettings'), icon: Settings },
        { to: '/admin/emergency-mode', label: t('staff.navEmergencyMode'), icon: ShieldAlert },
        { to: '/admin/hotlines', label: t('staff.navHotlines'), icon: Phone },
        { to: '/admin/responders', label: t('staff.navResponders'), icon: ClipboardList },
        { to: '/admin/disaster-events', label: t('staff.navDisasterEvents'), icon: CloudRain },
        { to: '/admin/categories', label: t('staff.navCategories'), icon: Tag },
        { to: '/admin/report-categories', label: t('staff.navReportCategories'), icon: Construction },
        { to: '/admin/holidays', label: t('staff.navHolidays'), icon: CalendarX },
        { to: '/admin/guides', label: t('staff.navTownGuides'), icon: BookOpenText },
        { to: '/admin/audit', label: t('staff.navAudit'), icon: History },
        { to: '/admin/sms-log', label: t('staff.navSmsLog'), icon: FileSpreadsheet },
      ]
    : [];

  const link = (i: NavItem) =>
    i.show === false ? null : (
      <NavLink key={i.to} to={i.to} end={i.to === ctx?.base || i.to === '/admin'} className="side-link">
        <i.icon size={18} aria-hidden="true" /> {i.label}
      </NavLink>
    );

  const switchOffice = (id: string) => {
    if (id === 'admin') return nav('/admin');
    const sub = mode === 'office' ? loc.pathname.split('/').slice(3).join('/') : '';
    const keep = ['posts', 'requests', 'services', 'checkin', 'hq', 'dispatch', 'crop', 'safety', 'profile', 'team', 'emergency-mode', 'alerts', 'revenue', 'feedback', 'guides', 'market-prices', 'recycling', 'facilities', 'moderation', 'tourism', 'transparency', 'ordinances'].find((p) => sub.startsWith(p)) ?? '';
    nav(`/staff/${id}${keep && !keep.startsWith('posts/') ? `/${keep}` : ''}`);
  };

  return (
    <div className="staff on-dark-header">
      <a className="skip-link" href="#staff-main">
        Skip to content
      </a>
      <header className="staff-header on-dark">
        <Link to={membership ? `/staff/${membership.officeId}` : '/staff'} className="brand">
          <IliMark size={22} variant="white" />
          <span className="nowrap">{t('nav.staffWorkspace')}</span>
        </Link>
        {(me.memberships.length > 0 || me.isAdmin) && (
          <>
            <label htmlFor="office-switcher" className="sr-only">
              {t('staff.switchOffice')}
            </label>
            <select id="office-switcher" className="office-switcher" value={mode === 'admin' ? 'admin' : String(officeId)} onChange={(e) => switchOffice(e.target.value)}>
              {me.memberships.map((m) => (
                <option key={m.officeId} value={m.officeId}>
                  {m.office.name} · {t(`staff.role_${m.role}`)}
                </option>
              ))}
              {me.isAdmin && <option value="admin">{t('nav.admin')}</option>}
            </select>
          </>
        )}
        <span className="grow" />
        <Link to="/" className="btn btn-sm btn-ghost" style={{ color: '#fff' }}>
          {t('nav.residentApp')}
        </Link>
      </header>
      <div className="staff-layout">
        <nav className="staff-nav" aria-label={t('nav.staffWorkspace')}>
          {ctx && (
            <div className="side-group">
              <h2>{membership!.office.shortName}</h2>
              {officeNav.map(link)}
            </div>
          )}
          {mode === 'admin' && adminNav.length > 0 && (
            <div className="side-group">
              <h2>{t('nav.admin')}</h2>
              {adminNav.map(link)}
            </div>
          )}
          {mode === 'office' && me.isAdmin && (
            <div className="side-group">
              <h2>{t('nav.admin')}</h2>
              {link({ to: '/admin', label: t('staff.navAdminHome'), icon: Gauge })}
            </div>
          )}
          <p className="xsmall muted" style={{ padding: '12px', marginTop: 'auto' }}>
            {s.displayName}
          </p>
        </nav>
        <main id="staff-main" className="staff-main" tabIndex={-1}>
          <Outlet context={ctx} />
        </main>
      </div>
    </div>
  );
}
